import { useSearchParams } from 'next/navigation';

import { NocoDbList } from '@/model/noco-db';
import { NocoDbMetadata } from '@/model/noco-db-metadata';

import { DataFetcher, DataFetcherMethod, DataFetcherProps } from './fetcher/DataFetcher';

export interface RepositoryGetProps extends Partial<DataFetcherProps> {
  id: number;
  lang?: string;
}
type RepositoryListProps = Partial<DataFetcherProps>;
export interface SearchProps<TSearchParams> extends Partial<DataFetcherProps> {
  search: TSearchParams;
  config?: SearchConfig;
}

interface SearchConfig {
  lang?: string;
}

// Command creators
const prepareWhereValue = (value: string) => {
  return value.replaceAll('&', '__ANDCOMMAND__');
};

export const createLikeCommand = (fieldName: string, value: string) =>
  `(${fieldName},like,"${prepareWhereValue(value)}")`;

export const createEqualCommand = (fieldName: string, value: string) => `(${fieldName},eq,${prepareWhereValue(value)})`;

export const createEqualQuotedCommand = (fieldName: string, value: string) =>
  `(${fieldName},eq,"${prepareWhereValue(value)}")`;

export const createExactDateCommand = (fieldName: string, value: string) =>
  `(${fieldName},eq,exactDate,${prepareWhereValue(value)})`;

export const createGeCommand = (fieldName: string, value: string) =>
  `(${fieldName},ge,exactDate,${prepareWhereValue(value)})`;

export const createLeCommand = (fieldName: string, value: string) =>
  `(${fieldName},le,exactDate,${prepareWhereValue(value)})`;

/** Join selected filter values so a comma inside one value is not a separator. */
export const serializeFilterValues = (values: string[]) => values.map(value => encodeURIComponent(value)).join(',');

export const parseFilterValues = (raw: string | null | undefined): string[] => {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map(part => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    })
    .filter(part => part.length > 0);
};

export const composeSearchObj = (searchParams: ReturnType<typeof useSearchParams>, filters): AbstractSearchParams => {
  const pageParam = searchParams?.get('page');
  const page = pageParam ? parseInt(pageParam, 10) : 1;

  return {
    page: page,
    _term: searchParams?.get('_term') || undefined,
    date_from: searchParams?.get('date_from') || undefined,
    date_to: searchParams?.get('date_to') || undefined,
    ...Object.fromEntries(filters.map(f => [f.key, searchParams?.get(f.key) || undefined]))
  };
};

export abstract class RepositoryNocoDbBase<T, TSearchParams> {
  protected abstract readonly API_ROUTE: string;
  protected abstract readonly META_ROUTE: string;
  protected abstract readonly SELECT_FILTERS: string[];
  protected abstract readonly SEARCH_FIELDS: (keyof T)[];
  protected readonly STATIC_FILTER: string = '';
  protected readonly DEFAULT_SORT: string = 'newest';
  /** Filters matched with eq instead of like (exact values). */
  protected readonly EQUAL_FILTERS: string[] = [];
  /** Multi-select fields matched with anyof, so one chosen value hits rows that carry several. */
  protected readonly ANY_OF_FILTERS: string[] = [];
  protected abstract readonly DATE_FIELD: keyof T;
  /** Sort by DATE_FIELD before CreatedAt (event date instead of entry date). */
  protected readonly SORT_BY_DATE_FIELD: boolean = false;
  protected abstract readonly RELEVANCE_FIELD: keyof T;
  protected constructor(protected _fetcher: DataFetcher) {}

  protected authHeaders = async () => {
    return {};
  };

  // protected abstract composeSearchRoute(search: TSearchParams): string;

  protected composeSearchRoute(search: TSearchParams, config?: SearchConfig) {
    const staticCase = this.STATIC_FILTER;
    const where: string[] = staticCase ? [staticCase] : [];

    // TODO: Statisch für demo-zwecke: jeder Filter (z.B. 'country') muss in einen loop
    this.SELECT_FILTERS.forEach(name => {
      if (!search) {
        return;
      }

      if (search[name]) {
        const values = parseFilterValues(String(search[name]));
        if (values.length === 0) {
          return;
        }
        const useEqual = this.EQUAL_FILTERS.includes(name);
        const useAnyOf = this.ANY_OF_FILTERS.includes(name);
        const subWhere = values.map((value: string) =>
          useAnyOf
            ? `(${name},anyof,${prepareWhereValue(value)})`
            : useEqual
              ? createEqualQuotedCommand(name, value)
              : createLikeCommand(name, value)
        );
        where.push(subWhere.length > 1 ? '(' + subWhere.join('~or') + ')' : subWhere[0]);
      }
    });

    for (const [key, value] of Object.entries(search ?? {})) {
      if (!value) continue; // Skip for empty values

      const searchFields: (keyof T)[] = this.SEARCH_FIELDS;
      let termArr;

      switch (key) {
        case '_term':
          // search with `_term` in multiple fields
          termArr = [];
          searchFields.map(field => termArr.push(createLikeCommand(field, value)));
          where.push('(' + termArr.join('~or') + ')');
          break;
        case 'date_from':
          if (this.DATE_FIELD) {
            where.push(createGeCommand(String(this.DATE_FIELD), String(value)));
          }
          break;
        case 'date_to':
          if (this.DATE_FIELD) {
            where.push(createLeCommand(String(this.DATE_FIELD), String(value)));
          }
          break;
        default:
          // For other fields, search in the exact same field name
          // where.push(createLikeCommand(key, value));
          break;
      }
    }

    let sort = this.DEFAULT_SORT;
    if (search) {
      sort = search['sort'] ? search['sort'] : this.DEFAULT_SORT;
    }
    let sortRoute = '';

    // By default sort by entry date (CreatedAt) only: a newly entered case with an older event date
    // must still show up first under "newest". DATE_FIELD is then only used for the date filter.
    const dateSort = this.SORT_BY_DATE_FIELD && this.DATE_FIELD ? String(this.DATE_FIELD) + ',' : '';
    switch (sort) {
      case 'oldest':
        sortRoute = dateSort + 'CreatedAt';
        break;
      case 'newest':
        sortRoute = (dateSort ? '-' + dateSort : '') + '-CreatedAt';
        break;
      case 'relevance':
      default:
        sortRoute =
          (this.RELEVANCE_FIELD ? '-' + String(this.RELEVANCE_FIELD) + ',' : '') +
          (dateSort ? '-' + dateSort : '') +
          '-CreatedAt';
        break;
    }

    const whereQuery = where.length > 0 ? '&where=@' + where.join('~and') : '';
    const limit = search?.limit ? '&limit=' + search.limit : '';

    const lang = config?.lang;

    return `${this.API_ROUTE}?page=${
      search?.page
    }${limit}${whereQuery}&sort=${sortRoute}${lang ? '&lang=' + lang : ''}`;
  }

  async searchAll({ search, config, ...fetchProps }: SearchProps<TSearchParams>) {
    let page = 1;
    const limit = 1000;
    const allData: T[] = [];
    let isLastPage = false;

    while (!isLastPage) {
      const url = this.composeSearchRoute({ ...search, page, limit }, config);

      const response = await this._fetcher.execute<NocoDbList<T>>({
        method: DataFetcherMethod.Get,
        url,
        headers: await this.authHeaders(),
        ...fetchProps
      });

      allData.push(...(response.data?.list || []));
      isLastPage = response.data?.pageInfo.isLastPage === true;
      page++;
    }

    return allData;
  }

  async search({ search, ...fetchProps }: SearchProps<TSearchParams>) {
    const url = this.composeSearchRoute(search);
    return (
      await this._fetcher.execute<NocoDbList<T>>({
        method: DataFetcherMethod.Get,
        url,
        headers: await this.authHeaders(),
        ...fetchProps
      })
    ).data;
  }

  async get({ id, lang, ...fetchProps }: RepositoryGetProps) {
    return (
      await this._fetcher.execute<T>({
        method: DataFetcherMethod.Get,
        url: `${this.API_ROUTE}/${id}${lang ? `?lang=${lang}` : ''}`,
        headers: await this.authHeaders(),
        ...fetchProps
      })
    ).data;
  }

  async getTableMetaInfo({ ...fetchProps }: RepositoryListProps) {
    return (
      await this._fetcher.execute<NocoDbList<NocoDbMetadata>>({
        method: DataFetcherMethod.Get,
        url: this.META_ROUTE,
        headers: await this.authHeaders(),
        ...fetchProps
      })
    ).data;
  }

  async getPageInfo() {
    // @ts-expect-error AbstractSearchParams muss als Basis für TSearchParams verwendet werden
    return (await this.search({ search: { page: 1 } }))?.pageInfo;
  }
}
